// Ctrl K: jump to any phase, task, deliverable or document by name. Searches only
// what the signed-in person already loaded through access-checked endpoints.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChartGantt, FileText, ListChecks, PackageCheck, Search } from 'lucide-react';
import { api } from '@/lib/api';
import { useTracker } from '@/lib/tracker';
import type { Program } from '@/lib/types';
import { cn } from '@/lib/utils';
import { Kbd } from '@/components/ui/misc';

interface Hit {
  kind: 'phase' | 'task' | 'deliverable' | 'document';
  id: string;
  label: string;
  hint: string;
  go: string;
}

const ICON = { phase: ChartGantt, task: ListChecks, deliverable: PackageCheck, document: FileText };

export function CommandPalette({ program }: { program: Program }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [i, setI] = useState(0);
  const nav = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  const tracker = useTracker(program.id);
  const docs = useQuery({
    queryKey: ['documents', program.id, 'palette'],
    queryFn: () =>
      api.get<Array<{ id: string; title: string; type: string }>>(
        `/api/programs/${program.id}/documents?fields=palette`,
      ),
    enabled: open,
    retry: false,
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setQ('');
      setI(0);
      setTimeout(() => input.current?.focus(), 0);
    }
  }, [open]);

  const hits = useMemo<Hit[]>(() => {
    const d = tracker.data;
    if (!d) return [];
    const base = `/p/${program.id}`;
    const all: Hit[] = [
      ...d.phases.map((p) => ({
        kind: 'phase' as const,
        id: p.id,
        label: `${p.number}. ${p.name}`,
        hint: 'Phase',
        go: `${base}/tracker/phase/${p.id}`,
      })),
      ...d.deliverables.map((x) => ({
        kind: 'deliverable' as const,
        id: x.id,
        label: x.name,
        hint: `Deliverable · Phase ${d.phases.find((p) => p.id === x.phaseId)?.number ?? ''}`,
        go: `${base}/deliverables/${x.id}`,
      })),
      ...d.tasks.map((t) => ({
        kind: 'task' as const,
        id: t.id,
        label: t.title,
        hint: `Task · Phase ${d.phases.find((p) => p.id === t.phaseId)?.number ?? ''}`,
        go: `${base}/tracker?task=${t.id}`,
      })),
      ...(Array.isArray(docs.data) ? docs.data : []).map((x) => ({
        kind: 'document' as const,
        id: x.id,
        label: x.title,
        hint: 'Document',
        go: `${base}/documents/${x.id}`,
      })),
    ];
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length)
      return all.filter((h) => h.kind === 'phase').concat(all.filter((h) => h.kind === 'deliverable').slice(0, 5));
    return all.filter((h) => words.every((w) => h.label.toLowerCase().includes(w))).slice(0, 30);
  }, [tracker.data, docs.data, q, program.id]);

  if (!open) return null;
  const go = (h: Hit) => {
    setOpen(false);
    nav(h.go);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 pt-[12vh]"
      onClick={() => setOpen(false)}
    >
      <div
        role="dialog"
        aria-label="Jump to"
        className="w-[min(640px,calc(100vw-32px))] overflow-hidden rounded-xl border border-border bg-surface shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-border px-4">
          <Search className="size-4 text-subtle" />
          <input
            ref={input}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setI(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setI((x) => Math.min(x + 1, hits.length - 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setI((x) => Math.max(x - 1, 0));
              } else if (e.key === 'Enter' && hits[i]) go(hits[i]);
              else if (e.key === 'Escape') setOpen(false);
            }}
            placeholder="Jump to a phase, task, deliverable or document"
            aria-label="Search"
            className="h-12 flex-1 bg-transparent text-[15px] outline-none placeholder:text-subtle"
          />
          <Kbd>Esc</Kbd>
        </div>
        <ul className="max-h-[50vh] overflow-y-auto p-1.5" role="listbox">
          {hits.map((h, n) => {
            const Icon = ICON[h.kind];
            return (
              <li key={`${h.kind}:${h.id}`} role="option" aria-selected={n === i}>
                <button
                  className={cn(
                    'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-[13.5px]',
                    n === i ? 'bg-brand-soft text-text' : 'hover:bg-surface-2',
                  )}
                  onMouseEnter={() => setI(n)}
                  onClick={() => go(h)}
                >
                  <Icon className="size-4 shrink-0 text-muted" />
                  <span className="flex-1 truncate">{h.label}</span>
                  <span className="shrink-0 text-xs text-subtle">{h.hint}</span>
                </button>
              </li>
            );
          })}
          {!hits.length && <li className="px-3 py-6 text-center text-sm text-muted">Nothing matches.</li>}
        </ul>
      </div>
    </div>
  );
}
